"use client";

import { useEffect, useState } from "react";

/** How long the real placeholder is readable before the typing starts. */
const START_DELAY_MS = 1200;
/** Per character, typing and deleting. Deleting is the faster of the two. */
const TYPE_MS = 45;
const DELETE_MS = 22;
/** How long a finished question stands before it is taken back. */
const HOLD_MS = 2400;
/** The caret, blinking only while a finished question stands. */
const CARET = "|";
const BLINK_MS = 520;

interface State {
  index: number;
  chars: number;
  /** `idle` is the pause before the first character, not a stopped state. */
  phase: "idle" | "typing" | "deleting";
}

const START: State = { index: 0, chars: 0, phase: "idle" };

/**
 * Types example questions into the resting Ask-Bar's placeholder, one
 * character at a time, as though someone were about to ask one.
 *
 * The point is that the Ask-Bar has to read as askable before anybody touches
 * it: a static field on a static hero looks like decoration. The questions are
 * the same page-aware openers the suggestion chips offer, so what is typed here
 * is always something the assistant can actually answer.
 *
 * Returns `null` whenever it should not be showing — before it starts, while
 * the visitor is in the field, and under `prefers-reduced-motion`. The caller
 * falls back to the real placeholder, which is also what the server renders, so
 * nothing here can shift hydration.
 */
export function useTypedPlaceholder(
  questions: readonly string[],
  enabled: boolean,
): string | null {
  const [state, setState] = useState<State>(START);
  const [caretOn, setCaretOn] = useState(true);

  const { index, chars, phase } = state;
  const text = questions.length > 0 ? questions[index % questions.length] : "";
  // Not a phase of its own: the question simply stands finished for a while.
  const holding = phase === "typing" && chars === text.length;

  // One step per render, and every step goes through a timer — the effect
  // itself never sets state, so a step can always be cancelled by the cleanup
  // and nothing here can cascade renders.
  useEffect(() => {
    if (!enabled) {
      // Back to the beginning, so the next time the field is left alone it
      // starts with a whole question rather than resuming mid-word. Nothing is
      // on screen while disabled, so this is invisible either way.
      if (phase === "idle" && chars === 0) return;
      const id = window.setTimeout(() => setState(START), 0);
      return () => window.clearTimeout(id);
    }
    if (!text) return;

    let delay: number;
    let next: State;
    if (phase === "idle") {
      delay = START_DELAY_MS;
      next = { index, chars, phase: "typing" };
    } else if (phase === "typing") {
      if (chars < text.length) {
        delay = TYPE_MS;
        next = { index, chars: chars + 1, phase };
      } else {
        delay = HOLD_MS;
        next = { index, chars, phase: "deleting" };
      }
    } else if (chars > 0) {
      delay = DELETE_MS;
      next = { index, chars: chars - 1, phase };
    } else {
      delay = TYPE_MS;
      next = { index: (index + 1) % questions.length, chars: 0, phase: "typing" };
    }

    const id = window.setTimeout(() => {
      setState(next);
      // Leaving a hold is where the caret is put back on, so the next hold
      // cannot begin on the dark half of a blink it did not start.
      if (next.phase === "deleting") setCaretOn(true);
    }, delay);
    return () => window.clearTimeout(id);
  }, [chars, enabled, index, phase, questions.length, text]);

  // Only while a question stands. A caret blinking through the typing itself
  // would swallow characters, and a timer running the whole time the hero is on
  // screen would re-render the composer twice a second for nothing.
  useEffect(() => {
    if (!holding) return;
    const id = window.setInterval(() => setCaretOn((v) => !v), BLINK_MS);
    return () => window.clearInterval(id);
  }, [holding]);

  if (!enabled || phase === "idle" || !text) return null;
  // A space, not nothing, on the dark half of the blink: dropping the character
  // would shift the whole line left and back every half second.
  return text.slice(0, chars) + (holding && !caretOn ? " " : CARET);
}
